import { CombinedGraphQLErrors } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useRef } from "react";
import type {
  GetShowLikeStateQuery,
  GetShowLikeStateQueryVariables,
  LikeMutation,
  LikeMutationVariables,
  UnlikeMutation,
  UnlikeMutationVariables,
} from "@/codegen/graphql/generated/graphql";
import { LIKE } from "@/codegen/graphql/mutations/Like.mutation";
import { UNLIKE } from "@/codegen/graphql/mutations/Unlike.mutation";
import { GET_SHOW_LIKE_STATE } from "@/codegen/graphql/queries/GetShowLikeState.query";
import { isStaleLikeError } from "@/lib/staleLike";
import { useAuthStore, useIsLoggedIn } from "@/stores/useAuth";
import { useAppStateStore } from "@/stores/useAppState";
import { useVideoPlayerStore } from "@/stores/useVideoPlayer";
import { useTracking } from "@/components/ClientAnalytics";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";

// Playback progress of the on-screen video, 0–100: the `percentage` the API
// stores next to the like. Read at tap time from the element `VideoVerticalPlayer`
// pushes into the store while it is on screen, so nothing has to be written per
// tick. A stream that hasn't loaded has no finite duration and reports 0.
const currentPlaybackPercentage = () => {
  const video = useVideoPlayerStore.getState().videoPlayer;
  if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(0, (video.currentTime / video.duration) * 100));
};

/**
 * Like / unlike wiring for the player's heart, on mobile and desktop alike.
 * Gates on a real account (not just an anonymous device token), de-dupes a
 * double tap while a mutation is in flight, updates the count optimistically
 * and fires `like episode` / `unlike episode` once the mutation resolves (not
 * on intent) so a failed call never reports a like.
 */
export default function useEpisodeLike(
  show: { id: string; title: string },
  episode: { id: string; number: number },
) {
  // Its own query, never `GetShow`: `liked` is @authenticated, see
  // GetShowLikeState.query.ts.
  const { data, refetch } = useQuery<
    GetShowLikeStateQuery,
    GetShowLikeStateQueryVariables
  >(GET_SHOW_LIKE_STATE, {
    variables: { showId: show.id },
    skip: !show.id,
  });
  // The query's own entry, not the caller's `episode`: the mutations write
  // `liked` and `likes` back to `Episode:{id}`, so this is what moves —
  // optimistically first, then as confirmed. The count is read nowhere else (see
  // GetShowLikeState.query.ts), so it is unknown until the query has answered.
  const state = data?.show.episodes.find((e) => e.id === episode.id);
  const liked = state?.liked ?? false;
  const likes = state?.likes;

  const isLoggedIn = useIsLoggedIn();
  const openLoginModal = useAppStateStore((s) => s.openLoginModal);
  const { trackAmplitudeEvent } = useTracking();
  const pendingRef = useRef(false);

  const [like] = useMutation<LikeMutation, LikeMutationVariables>(LIKE);
  const [unlike] = useMutation<UnlikeMutation, UnlikeMutationVariables>(UNLIKE);

  const send = async () => {
    // Before the like state has answered, neither the count to move nor which
    // of Like / Unlike to send is known.
    if (pendingRef.current || !state) return;
    pendingRef.current = true;

    const variables = {
      id: episode.id,
      percentage: currentPlaybackPercentage(),
    };
    const optimistic = { __typename: "Episode" as const, id: episode.id };

    try {
      if (liked) {
        await unlike({
          variables,
          optimisticResponse: {
            unlike: {
              ...optimistic,
              liked: false,
              likes: Math.max(0, state.likes - 1),
            },
          },
        });
      } else {
        await like({
          variables,
          optimisticResponse: {
            like: { ...optimistic, liked: true, likes: state.likes + 1 },
          },
        });
      }
      trackAmplitudeEvent(
        liked
          ? AmplitudeTrackingEvent.unlikeEpisode
          : AmplitudeTrackingEvent.likeEpisode,
        {
          "series name": show.title,
          "episode number": episode.number.toString(),
          origin: "player",
        },
      );
    } catch (error) {
      // A stale toggle: the server holds the state, so read it back. Anything
      // else was reported by `errorLink` already (a transport failure is left
      // unreported on purpose, see apiClient.ts).
      if (
        CombinedGraphQLErrors.is(error) &&
        error.errors.some(isStaleLikeError)
      ) {
        void refetch();
      }
    } finally {
      pendingRef.current = false;
    }
  };

  const toggle = () => {
    if (!isLoggedIn) {
      openLoginModal({
        origin: "player",
        // The login modal fires its callback on ANY close, a dismissal
        // included, so re-read the real auth state: an Escape must not like
        // on the anonymous device token (which the backend's @authenticated
        // accepts) and report a `like episode` the user declined.
        callback: () => {
          if (useAuthStore.getState().currentUser?.providers.length) send();
        },
      });
      return;
    }
    send();
  };

  return { toggle, liked, likes };
}
